import {
  ClaimStatus,
  OutreachShift,
  ShiftClaimContextState,
  ShiftClaimUrlParams,
} from '../types/shiftClaim';
import {ShiftClaimContext, initialState} from '../context/shiftClaim';
import {
  claimShift as claimShiftApi,
  fetchAvailableShifts,
  rejectShift as rejectShiftApi,
} from '../api/shiftClaimApi';
import {useCallback, useContext, useMemo, useReducer} from 'react';

import {PayloadAction} from '@reduxjs/toolkit/dist/createAction';
import {createSlice} from '@reduxjs/toolkit';
import {viewToast} from '../../components/composites/notification';

const slice = createSlice({
  initialState,
  name: 'shiftClaimContext',
  reducers: {
    setFetchStatus(
      state: ShiftClaimContextState,
      action: PayloadAction<ClaimStatus>,
    ) {
      state.fetchStatus = action.payload;
    },
    setShiftsSuccess(
      state: ShiftClaimContextState,
      action: PayloadAction<OutreachShift[]>,
    ) {
      state.shifts = action.payload;
      state.fetchStatus = ClaimStatus.Idle;
    },
    addClaimedId(state: ShiftClaimContextState, action: PayloadAction<string>) {
      if (!state.claimedIds.includes(action.payload))
        state.claimedIds.push(action.payload);
    },
    setClaimingId(
      state: ShiftClaimContextState,
      action: PayloadAction<string | null>,
    ) {
      state.claimingId = action.payload;
    },
    addRejectedId(
      state: ShiftClaimContextState,
      action: PayloadAction<string>,
    ) {
      if (!state.rejectedIds.includes(action.payload))
        state.rejectedIds.push(action.payload);
      state.claimedIds = state.claimedIds.filter(id => id !== action.payload);
    },
    setRejectingId(
      state: ShiftClaimContextState,
      action: PayloadAction<string | null>,
    ) {
      state.rejectingId = action.payload;
    },
  },
});

export const ShiftClaimActions = slice.actions;

export function useShiftClaimReducer() {
  return useReducer(slice.reducer, initialState);
}

export function useShiftClaimContext() {
  return useContext(ShiftClaimContext);
}

export function useShiftClaimUrlParams(): ShiftClaimUrlParams {
  return useMemo(() => {
    const params = new URLSearchParams(
      (typeof window !== 'undefined' && window?.location?.search) || '',
    );
    return {wuserId: params.get('wuserid') ?? ''};
  }, []);
}

export function useFetchAvailableShifts() {
  const {dispatch} = useShiftClaimContext();

  const fetchShifts = useCallback(
    async (wuserId: string) => {
      if (!wuserId) {
        dispatch?.(ShiftClaimActions.setFetchStatus(ClaimStatus.Invalid));
        return;
      }
      dispatch?.(ShiftClaimActions.setFetchStatus(ClaimStatus.Loading));
      try {
        const shifts = await fetchAvailableShifts(wuserId);
        dispatch?.(ShiftClaimActions.setShiftsSuccess(shifts));
      } catch (err: any) {
        viewToast({title: err?.message || 'Failed to load shifts'}, 3000);
        dispatch?.(ShiftClaimActions.setFetchStatus(ClaimStatus.NetworkError));
      }
    },
    [dispatch],
  );

  return {fetchShifts};
}

export function useClaimShift() {
  const {dispatch} = useShiftClaimContext();

  const claimShift = useCallback(
    async (entityId: string) => {
      if (!entityId) return;
      dispatch?.(ShiftClaimActions.setClaimingId(entityId));
      try {
        await claimShiftApi(entityId);
        dispatch?.(ShiftClaimActions.addClaimedId(entityId));
      } catch (err: any) {
        viewToast({title: err?.message || 'Failed to claim shift'}, 3000);
      } finally {
        dispatch?.(ShiftClaimActions.setClaimingId(null));
      }
    },
    [dispatch],
  );

  return {claimShift};
}

export function useRejectShift() {
  const {dispatch} = useShiftClaimContext();

  const rejectShift = useCallback(
    async (entityId: string, status: string) => {
      if (!entityId) return;
      dispatch?.(ShiftClaimActions.setRejectingId(entityId));
      try {
        await rejectShiftApi(entityId, status);
        dispatch?.(ShiftClaimActions.addRejectedId(entityId));
      } catch (err: any) {
        viewToast({title: err?.message || 'Failed to reject shift'}, 3000);
      } finally {
        dispatch?.(ShiftClaimActions.setRejectingId(null));
      }
    },
    [dispatch],
  );

  return {rejectShift};
}
